/**
 * A note's text as it is stored, drawn and read as words (piece 2; decision 7180).
 *
 * 🔒 A note's text holds sanitized HTML under the notes' allowance, or the plain
 * words an older note and every plain box wrote. Nothing marks which: a text
 * carrying an element of the allowance is rich, and any other text is plain and
 * reads as it is. 🔒 Two derivations read it (decisions 7180, 7290). `noteWords` gives
 * the words every reader of a line uses: the History row, a stage card's answer, a
 * request's need and the RFQ's message. `noteMarkdown` gives the same note with its
 * bold, italic and links as Markdown to the readers that hand a document to a person
 * or to Claude: the Deal Pack, Copy for Claude and the text Ask Claude sends.
 */
import { RICH_TEXT_ALIGNMENTS, RICH_TEXT_TAGS, escapeHtml, richTextFromPlain, richTextStartOf, sanitizeRichText, type RichTextAllowance } from '../controls/richText';
import { articleColourOf } from '../knowledge/body';
import { mailPlainText } from '../mail/body';

/**
 * 🔒 The notes' allowed table (decision 7180): the editor's own tags, a span and
 * a `font` element for the colour an email carries, and no picture and no table.
 */
export const NOTE_RICH_TEXT_TAGS: Readonly<Record<string, readonly string[]>> = {
  ...RICH_TEXT_TAGS,
  span: ['style'],
  font: ['color'],
};

/**
 * 🔒 The colour names a mail program writes for its basic palette, read as the
 * hex digits a stored note keeps; any other name is dropped.
 */
export const NOTE_COLOUR_NAMES: Readonly<Record<string, string>> = {
  black: '#000000', white: '#ffffff', red: '#ff0000', green: '#008000', blue: '#0000ff', yellow: '#ffff00',
  orange: '#ffa500', purple: '#800080', gray: '#808080', grey: '#808080', silver: '#c0c0c0', maroon: '#800000',
  navy: '#000080', teal: '#008080', olive: '#808000', lime: '#00ff00', aqua: '#00ffff', fuchsia: '#ff00ff',
};

/** A colour as six lower-case hex digits: a hex or rgb value, or one of the palette's names; null for anything else. */
export function noteColourOf(raw: string): string | null {
  const value = raw.replace(/!important/i, '').trim().toLowerCase();
  return articleColourOf(value) ?? NOTE_COLOUR_NAMES[value] ?? null;
}

/** The declarations a note's kept style may carry, in the one order they are written back. */
export const NOTE_STYLE_PROPERTIES = ['text-align', 'color', 'background-color'] as const;
type NoteStyleProperty = (typeof NOTE_STYLE_PROPERTIES)[number];

/**
 * 🔒 A style read as a note keeps it (decision 7180, amending 0770 for notes): a
 * block's alignment, the text colour and the highlight. 🔨 A size, a font, a
 * margin and every other declaration a pasted email brought is dropped.
 */
export function noteStyleOf(style: string): string | null {
  const found = new Map<NoteStyleProperty, string>();
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const property = declaration.slice(0, colon).trim().toLowerCase() as NoteStyleProperty;
    const value = declaration.slice(colon + 1).trim().toLowerCase();
    if (found.has(property)) continue;
    if (property === 'text-align' && (RICH_TEXT_ALIGNMENTS as readonly string[]).includes(value)) found.set(property, value);
    if (property === 'color' || property === 'background-color') {
      const colour = noteColourOf(value);
      if (colour) found.set(property, colour);
    }
  }
  const kept = NOTE_STYLE_PROPERTIES.filter((property) => found.has(property)).map((property) => `${property}: ${found.get(property)};`);
  return kept.length ? kept.join(' ') : null;
}

/** The notes' allowance: their table, their style reader, and a `font` colour and a list's start checked. */
export const NOTE_ALLOWANCE: RichTextAllowance = {
  tags: NOTE_RICH_TEXT_TAGS,
  style: noteStyleOf,
  attribute: (name, value) => {
    if (name === 'color') return noteColourOf(value);
    if (name === 'start') return richTextStartOf(value);
    return value.trim();
  },
};

/** An element of the allowance, opening or closing: what makes a note's text rich. */
const RICH_ELEMENT = new RegExp(`<\\/?(?:${Object.keys(NOTE_RICH_TEXT_TAGS).sort((a, b) => b.length - a.length).join('|')})(?=[\\s>/])[^>]*>`, 'i');

/** True where a note's text holds markup of the allowance; an older note and a plain box's text hold none and read as they are. */
export function noteIsRich(text: string | null | undefined): boolean {
  return Boolean(text) && RICH_ELEMENT.test(text!);
}

/** An element whose content is code or a document's head rather than words: the element and what it holds both go. */
const CODE_ELEMENTS = /<(script|style|iframe|object|embed|noscript|template|head|title|xml)\b[\s\S]*?<\/\1\s*>/gi;
/** A namespaced element an office program writes, such as `o:p`: the element goes and its words stay. */
const NAMESPACED = /<\/?[a-zA-Z][\w-]*:[\w.-]+\b[^>]*>/g;
/** A tag with attributes quoted either way or not at all, as a mail program writes one. */
const LOOSE_TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+))?)*)\s*(\/?)>/g;
const LOOSE_ATTRIBUTE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;

/**
 * Every tag written the one way the allowlist reads: each attribute double-quoted, its quote and brackets as entities,
 * and an attribute with no value or a namespaced name dropped. 💰 A pasted Outlook paragraph reads `<p class=MsoNormal>`,
 * which the allowlist would otherwise escape into visible text.
 */
function quotedTags(html: string): string {
  return html.replace(LOOSE_TAG, (_whole, closing: string, name: string, attributes: string, selfClosing: string) => {
    if (closing) return `</${name}>`;
    let out = '';
    for (const match of attributes.matchAll(LOOSE_ATTRIBUTE)) {
      const attribute = match[1]!;
      const value = match[2] ?? match[3] ?? match[4];
      if (value === undefined || !/^[a-zA-Z][a-zA-Z0-9-]*$/.test(attribute)) continue;
      out += ` ${attribute}="${value.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}"`;
    }
    return `<${name}${out}${selfClosing ? ' /' : ''}>`;
  });
}

/**
 * 🔒 Markup made safe as a note's (decision 7180): the code, the head and an office program's own elements gone, every
 * tag quoted one way, then the notes' allowance. Always markup: for a surface, a view or a paste, never a plain store.
 */
export function sanitizeNoteText(html: string): string {
  if (!html) return '';
  return sanitizeRichText(quotedTags(html.replace(CODE_ELEMENTS, '').replace(NAMESPACED, '')), NOTE_ALLOWANCE);
}

/** A note's text as markup to draw or to edit: rich text through the allowance, plain words escaped with their lines kept. */
export function noteHtml(text: string): string {
  return noteIsRich(text) ? sanitizeNoteText(text) : richTextFromPlain(text);
}

/**
 * 🔒 What a note write stores (decision 7180): plain words trimmed and kept as typed, rich text through the allowance.
 * Rich text the allowance leaves with no element is stored as its words, and rich text with no words stores nothing.
 */
export function noteTextToStore(text: string | null | undefined): string {
  const trimmed = (text ?? '').trim();
  if (!noteIsRich(trimmed)) return trimmed;
  const safe = sanitizeNoteText(trimmed).trim();
  if (noteWords(safe) === '') return '';
  if (!noteIsRich(safe)) return noteWords(safe);
  return noteFoldedWords(safe) ?? safe;
}

/** One paragraph or division with no attribute, holding only words and line breaks. */
const PLAIN_BLOCK = /<(p|div)>((?:[^<]|<br\s*\/?>)*)<\/\1>/gi;

/**
 * 🔒 A rich note that carries no formatting, as its words (decision 7290, T3053; none where the words would read as markup): markup made only of bare paragraphs
 * and line breaks, which is what the editor writes for plain typing, reads as a paragraph a blank line apart and a
 * line break a line. Null where the markup holds a mark, a colour, a link, a list, a heading, a quotation or an alignment.
 */
export function noteFoldedWords(text: string): string | null {
  const html = text.trim();
  if (html === '' || html.replace(PLAIN_BLOCK, '') !== '') return null;
  const paragraphs = [...html.matchAll(PLAIN_BLOCK)]
    // A source newline inside a paragraph is a space, as a browser draws it; only a line break element starts a line (T3055).
    .map((match) => decodeEntities(match[2]!.replace(/\s+/g, ' ').replace(/<br\s*\/?>/gi, '\n')).split('\n').map((line) => line.trim()).join('\n').trim())
    .filter((paragraph) => paragraph !== '');
  const words = paragraphs.join('\n\n');
  // Words that would themselves read as markup, such as a typed `<b>`, stay in the markup that escapes them.
  return noteIsRich(words) ? null : words;
}

/** True where a note's text carries no words at all, such as an editor emptied to one bare paragraph. */
export function noteIsBlank(text: string | null | undefined): boolean {
  return noteWords(text ?? '') === '';
}

/**
 * A rich surface's markup as a note write sends it: words the surface held outside any block are wrapped in a
 * paragraph, so the server reads the text as rich and its entities as the characters they stand for.
 */
export function noteFromSurface(html: string): string {
  const trimmed = html.trim();
  if (trimmed === '' || noteIsBlank(trimmed)) return '';
  return noteIsRich(trimmed) ? trimmed : `<p>${trimmed}</p>`;
}

/**
 * 🔒 Body as Note's text (decisions 7180, amending piece 32's plain strip): the subject as the first paragraph, then the
 * message's body under the notes' allowance, so its bold, colours and lists stay. A message whose body keeps no
 * element of the allowance, such as one table or one preformatted block, is written as before: the subject, a blank
 * line and the body's words, never its markup.
 */
export function noteFromMail(subject: string, bodyHtml: string): string {
  const title = subject.trim();
  const body = noteTextToStore(bodyHtml);
  if (!noteIsRich(body)) return [title, noteIsRich(bodyHtml) ? body : mailPlainText(bodyHtml)].filter(Boolean).join('\n\n');
  return `${title ? `<p>${escapeHtml(title)}</p>` : ''}${body}`;
}

/** One run of a note's words with the formatting the allowance kept on it. */
export interface NoteRun {
  text: string;
  bold?: true;
  italic?: true;
  underline?: true;
  strike?: true;
  /** Six hex digits, as the allowance writes them. */
  color?: string;
  background?: string;
  href?: string;
}

/** One line of a note as the phone draws it: a paragraph, a heading, a quotation or a list item with its marker. */
export interface NoteBlock {
  kind: 'paragraph' | 'heading' | 'quote' | 'item';
  runs: NoteRun[];
  /** How deep the list stands, 1 for a list's own items and 0 outside a list. */
  depth: number;
  /** A list item's marker: a bullet, or its number in a numbered list; null on a continuation and outside a list. */
  marker: { ordered: boolean; number: number } | null;
  /** A heading's level, 1 to 4; absent on every other block. */
  level?: number;
  /** The block's alignment where the allowance kept a `text-align` on it; absent on a block left to its default. */
  align?: NoteAlign;
}

/** The alignments the notes' allowance keeps on a block. */
export type NoteAlign = 'left' | 'center' | 'right' | 'justify';

const ENTITIES: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, name: string) => {
    if (name[0] === '#') {
      const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : Number(name.slice(1));
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[name.toLowerCase()] ?? whole;
  });
}

const TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*)>|([^<]+)/g;
/** The alignment a block's opening tag carries in its style, or none. */
function alignOf(attributes: string): NoteAlign | undefined {
  const style = /\bstyle="([^"]*)"/.exec(attributes)?.[1] ?? '';
  const found = /(?:^|;)\s*text-align:\s*(left|center|right|justify)\b/.exec(style)?.[1];
  return found as NoteAlign | undefined;
}

const BLOCKS = new Set(['p', 'div', 'h1', 'h2', 'h3', 'h4', 'blockquote', 'li']);
type Format = Omit<NoteRun, 'text'>;

/** The formatting one opening tag lays over the words inside it. */
function formatOf(name: string, attributes: string): Format {
  const format: Format = {};
  if (name === 'b' || name === 'strong') format.bold = true;
  if (name === 'i' || name === 'em') format.italic = true;
  if (name === 'u') format.underline = true;
  if (name === 's' || name === 'strike') format.strike = true;
  if (name === 'a') {
    const href = /\bhref="([^"]*)"/.exec(attributes)?.[1];
    if (href) format.href = decodeEntities(href);
  }
  const style = /\bstyle="([^"]*)"/.exec(attributes)?.[1] ?? '';
  const colour = /(?:^|;)\s*color:\s*(#[0-9a-f]{6})/.exec(style)?.[1] ?? (name === 'font' ? /\bcolor="(#[0-9a-f]{6})"/.exec(attributes)?.[1] : undefined);
  if (colour) format.color = colour;
  const background = /background-color:\s*(#[0-9a-f]{6})/.exec(style)?.[1];
  if (background) format.background = background;
  return format;
}

/**
 * 🔒 A note's lines with their formatting (decision 7180): what the phone's renderer draws and what `noteWords` reads.
 * A plain note is one paragraph a line. The text passes the allowance first, so nothing else reaches a reader.
 */
export function noteBlocks(text: string): NoteBlock[] {
  if (!noteIsRich(text)) {
    return text.replace(/\r\n/g, '\n').split('\n').filter((line, index, all) => line.trim() !== '' || (index > 0 && index < all.length - 1)).map((line) => ({ kind: 'paragraph' as const, runs: line ? [{ text: line }] : [], depth: 0, marker: null }));
  }
  const html = sanitizeNoteText(text);
  const blocks: NoteBlock[] = [];
  const formats: { name: string; format: Format }[] = [];
  const lists: { ordered: boolean; next: number }[] = [];
  let quotes = 0;
  let current: NoteBlock | null = null;
  const kindHere = (name: string): NoteBlock['kind'] => (/^h[1-4]$/.test(name) ? 'heading' : quotes > 0 ? 'quote' : lists.length > 0 ? 'item' : 'paragraph');
  const flush = () => {
    if (current) {
      const last = current.runs[current.runs.length - 1];
      if (last) last.text = last.text.replace(/[ \t]+$/, '');
      current.runs = current.runs.filter((run) => run.text !== '');
      if (current.runs.some((run) => run.text.trim() !== '')) blocks.push(current);
    }
    current = null;
  };
  const open = (kind: NoteBlock['kind'], marker: NoteBlock['marker']) => {
    flush();
    current = { kind, runs: [], depth: lists.length, marker };
  };
  for (const match of html.matchAll(TOKEN)) {
    const [, closing, rawName, attributes = '', words] = match;
    if (words !== undefined) {
      let piece = decodeEntities(words).replace(/\s+/g, ' ');
      if (!current) open(quotes > 0 ? 'quote' : lists.length > 0 ? 'item' : 'paragraph', null);
      const block = current as unknown as NoteBlock;
      const previous = block.runs[block.runs.length - 1];
      if (!previous || previous.text.endsWith('\n') || previous.text.endsWith(' ')) piece = piece.replace(/^ /, '');
      if (piece === '') continue;
      const format = Object.assign({}, ...formats.map((entry) => entry.format)) as Format;
      block.runs.push({ text: piece, ...format });
      continue;
    }
    const name = rawName!.toLowerCase();
    if (closing) {
      if (name === 'ul' || name === 'ol') {
        flush();
        lists.pop();
      } else if (name === 'blockquote') {
        flush();
        quotes = Math.max(0, quotes - 1);
      } else if (BLOCKS.has(name)) flush();
      const at = formats.map((entry) => entry.name).lastIndexOf(name);
      if (at >= 0) formats.splice(at, 1);
      continue;
    }
    if (name === 'br') {
      if (!current) open(kindHere(name), null);
      (current as unknown as NoteBlock).runs.push({ text: '\n' });
      continue;
    }
    if (name === 'ul' || name === 'ol') {
      flush();
      lists.push({ ordered: name === 'ol', next: Number(/\bstart="(\d+)"/.exec(attributes)?.[1] ?? 1) });
      continue;
    }
    if (name === 'blockquote') {
      flush();
      quotes += 1;
    }
    if (name === 'li') {
      const list = lists[lists.length - 1];
      open('item', list ? { ordered: list.ordered, number: list.next++ } : { ordered: false, number: 1 });
    } else if (BLOCKS.has(name) && name !== 'blockquote') {
      // A paragraph opened inside a list item that holds no words yet is that item's own line, marker and all.
      const held = current as NoteBlock | null;
      if (!(held && held.kind === 'item' && held.runs.length === 0)) {
        open(kindHere(name), null);
        if (/^h[1-4]$/.test(name)) (current as unknown as NoteBlock).level = Number(name[1]);
      }
    }
    // The alignment the block's own tag carries (T3313); a paragraph inside an item aligns that item's line.
    if (BLOCKS.has(name) && current) {
      const align = alignOf(attributes);
      if (align) (current as unknown as NoteBlock).align = align;
    }
    formats.push({ name, format: formatOf(name, attributes) });
  }
  flush();
  for (const block of blocks) {
    const last = block.runs[block.runs.length - 1];
    if (last) last.text = last.text.replace(/\s+$/, '');
    const first = block.runs[0];
    if (first) first.text = first.text.replace(/^\s+/, '');
    block.runs = block.runs.filter((run) => run.text !== '');
  }
  return blocks;
}

/** A block's words, a link's address after the last of its words where the words do not already say it. */
function linkedWords(runs: readonly NoteRun[]): string {
  let out = '';
  let linked = '';
  runs.forEach((run, index) => {
    out += run.text;
    if (!run.href) return;
    linked += run.text;
    if (runs[index + 1]?.href === run.href) return;
    const said = linked.trim();
    if (run.href !== said && run.href !== `mailto:${said}` && run.href !== `tel:${said}`) out += ` (${run.href})`;
    linked = '';
  });
  return out;
}

/**
 * 🔒 A note's plain words (decision 7180): the one derivation every reader that needs words uses. A plain note reads as
 * it is. A rich note reads a line per block, a list item behind its bullet or number, and a link's address after its words.
 * 💰 A colour that told two things apart is not in the words.
 */
export function noteWords(text: string): string {
  if (!noteIsRich(text)) return text.trim();
  return noteBlocks(text)
    .map((block) => {
      const indent = block.depth > 1 ? '  '.repeat(block.depth - 1) : '';
      const marker = block.marker ? (block.marker.ordered ? `${block.marker.number}. ` : '- ') : block.kind === 'item' ? '  ' : '';
      const words = linkedWords(block.runs);
      const quoted = block.kind === 'quote' ? `> ${words}` : words;
      return `${indent}${marker}${quoted}`.replace(/[ \t]+\n/g, '\n');
    })
    .join('\n')
    .trim();
}

/** What `noteMarkdown` takes: how tight its lines sit and where its headings start. */
export interface NoteMarkdownOptions {
  /** True to put every block on the next line with no blank line between, as `noteWords` does; a reader that splits a note into items takes this. */
  tight?: boolean;
  /** The level a note's top heading is written at, 1 by default; a pack that owns its own headings writes a note's below them. */
  headingBase?: number;
}

/** The marks that decide how a run is wrapped, so runs that differ only in colour or underline join into one. */
const markKey = (run: NoteRun): string => `${run.bold ? 'b' : ''}${run.italic ? 'i' : ''}${run.strike ? 's' : ''}`;

/** A literal `*`, `_`, `~` or `[` at the edge of a run's words, backslashed so it does not read as formatting; every other character stays as typed. */
function guardEdges(words: string): string {
  const mark = /[*_~[]/;
  // The edges are decided on the run as it came, so a run of one mark character is backslashed once.
  const first = mark.test(words[0] ?? '');
  const last = words.length > 1 && mark.test(words[words.length - 1]!);
  return `${first ? '\\' : ''}${words.slice(0, last ? -1 : undefined)}${last ? `\\${words[words.length - 1]}` : ''}`;
}

/** Runs of one stretch with no link, joined where their marks match, each wrapped in its marks with the spaces left outside them. */
function markedRuns(runs: readonly NoteRun[]): string {
  const joined: { key: string; run: NoteRun }[] = [];
  for (const run of runs) {
    const last = joined[joined.length - 1];
    if (last && last.key === markKey(run)) last.run = { ...last.run, text: last.run.text + run.text };
    else joined.push({ key: markKey(run), run: { ...run } });
  }
  return joined.map(({ run }) => {
    const [, lead = '', core = '', trail = ''] = /^(\s*)([\s\S]*?)(\s*)$/.exec(run.text) ?? [];
    if (core === '') return run.text;
    let words = guardEdges(core);
    if (run.italic) words = `_${words}_`;
    if (run.bold) words = `**${words}**`;
    if (run.strike) words = `~~${words}~~`;
    return `${lead}${words}${trail}`;
  }).join('');
}

/** A link's address written so a space or a bracket in it does not end the link early. */
const markdownAddress = (href: string): string => href.replace(/ /g, '%20').replace(/\(/g, '%28').replace(/\)/g, '%29');

/** A block's runs as Markdown: a link as `[words](address)`, or `<address>` where its words are the address. */
function markdownRuns(runs: readonly NoteRun[]): string {
  let out = '';
  for (let at = 0; at < runs.length; ) {
    const href = runs[at]!.href;
    let end = at + 1;
    while (end < runs.length && runs[end]!.href === href) end += 1;
    const group = runs.slice(at, end);
    at = end;
    if (!href) {
      out += markedRuns(group);
      continue;
    }
    const words = group.map((run) => run.text).join('');
    const said = words.trim();
    if (said === href || `mailto:${said}` === href) {
      out += `${/^\s*/.exec(words)![0]}<${said}>${/\s*$/.exec(words)![0]}`;
      continue;
    }
    const [, lead = '', , trail = ''] = /^(\s*)([\s\S]*?)(\s*)$/.exec(words) ?? [];
    const inner = markedRuns(group);
    // A `]` in the link's words is backslashed, or it would end the words early (T3055).
    out += `${lead}[${inner.trim().replace(/\]/g, '\\]')}](${markdownAddress(href)})${trail}`;
  }
  return out;
}

/**
 * 🔒 A note's formatting as Markdown (decision 7290, amending 7180): the second derivation, for the readers that hand a
 * note to a person or to Claude as a document. Bold is `**words**`, italic `_words_`, strikethrough `~~words~~`, a link
 * `[words](address)`, an underline the words alone; a list item stands behind `- ` or its number, a nested one at its parent's content offset, a heading behind its `#` marks and a quotation behind `> `. Blocks stand a blank line apart, a list's items
 * on consecutive lines. 💰 A colour and a highlight are dropped, as Markdown has none. A plain note reads as it is, and a
 * literal `*`, `_`, `~` or `[` at the edge of a run is backslashed; one inside a run is left as typed.
 */
export function noteMarkdown(text: string, options: NoteMarkdownOptions = {}): string {
  if (!noteIsRich(text)) return text.trim();
  const base = Math.min(Math.max(options.headingBase ?? 1, 1), 6);
  let out = '';
  let previous: NoteBlock | null = null;
  const markerWidths: number[] = [];
  for (const block of noteBlocks(text)) {
    // 🔒 T3055: a nested list stands at its parent's content offset (CommonMark), the width of the parent's own marker: two for a bullet, three for `1. `, four for `10. `.
    let indent = '';
    for (let level = 1; level < block.depth; level += 1) indent += ' '.repeat(markerWidths[level] ?? 2);
    let marker = '';
    if (block.kind === 'item') {
      if (block.marker) {
        marker = block.marker.ordered ? `${block.marker.number}. ` : '- ';
        markerWidths[block.depth] = marker.length;
        markerWidths.length = block.depth + 1;
      } else marker = ' '.repeat(markerWidths[block.depth] ?? 2);
    }
    const prefix = block.kind === 'heading' ? `${'#'.repeat(Math.min(base + (block.level ?? 1) - 1, 6))} ` : block.kind === 'quote' ? '> ' : '';
    const words = markdownRuns(block.runs).replace(/[ \t]*\n[ \t]*/g, `  \n${indent}${block.kind === 'item' ? ' '.repeat(marker.length) : ''}${block.kind === 'quote' ? '> ' : ''}`);
    const line = `${indent}${marker}${prefix}${words}`;
    const together = options.tight === true || (previous !== null && previous.kind === 'item' && block.kind === 'item') || (previous !== null && previous.kind === 'quote' && block.kind === 'quote');
    out += previous === null ? line : `${together ? '\n' : '\n\n'}${line}`;
    previous = block;
  }
  return out.trim();
}
